'use client';

import React, { useEffect, useRef, useState } from 'react';
import { GripVertical, Trash2, Plus, X } from 'lucide-react';
import { clsx } from 'clsx';
import { resolveLabelColor, type TagColorId } from './tag-colors';
import { TagColorSwatches } from './tag-color-swatches';

/**
 * Minimale strukturelle Form eines verwaltbaren Labels. Sowohl
 * `MaterialTemplateLabel` (Projekte) als auch `FavoriteLabel` (Favoriten) sind
 * hierzu strukturell zuweisbar, daher bleibt die Komponente generisch.
 */
export interface ManageableLabel {
  name: string;
  color: string | null;
  sortOrder: number;
}

export interface TagManageContentProps {
  /** Labels in persistierter Reihenfolge (sortOrder). */
  labels: ManageableLabel[];
  /** Projekt-Anzahl je Label-Name (für die `N×`-Zähler). */
  counts: Record<string, number>;
  onRename: (oldName: string, newName: string) => void;
  onRecolor: (name: string, color: TagColorId) => void;
  onReorder: (orderedNames: string[]) => void;
  onCreate: (name: string, color: TagColorId) => void;
  onDelete: (name: string) => void;
  /** Substantiv für den Verwendungszähler in der Lösch-Bestätigung. */
  usageNoun?: { singular: string; plural: string };
}

const DEFAULT_NEW_COLOR: TagColorId = 'blue';

/**
 * Geteilter Inhalt des „Labels verwalten"-Flows — identisch in mobilem
 * BottomSheet und Desktop-Modal. Eine Zeile pro Label: Drag-Griff, antippbarer
 * Farbpunkt (Inline-Palette), editierbarer Name (commit bei Blur/Enter),
 * Verwendungszähler und Löschen (Inline-Bestätigung bei genutzten Labels). Am
 * Ende „+ Neues Label".
 */
export function TagManageContent({
  labels,
  counts,
  onRename,
  onRecolor,
  onReorder,
  onCreate,
  onDelete,
  usageNoun,
}: TagManageContentProps) {
  const { singular, plural } = usageNoun ?? { singular: 'Projekt', plural: 'Projekten' };

  // Lokale, optimistische Reihenfolge — folgt dem Server, solange nicht gezogen wird.
  const [order, setOrder] = useState<string[]>(() => labels.map((l) => l.name));
  const [dragName, setDragName] = useState<string | null>(null);
  const [expandedColor, setExpandedColor] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<TagColorId>(DEFAULT_NEW_COLOR);
  const newInputRef = useRef<HTMLInputElement>(null);

  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});

  const labelByName = new Map(labels.map((l) => [l.name, l]));

  useEffect(() => {
    if (!dragName) setOrder(labels.map((l) => l.name));
  }, [labels, dragName]);

  useEffect(() => {
    if (adding) newInputRef.current?.focus();
  }, [adding]);

  // Drag-Sortierung über Pointer-Events (keine DnD-Lib)
  const handleGripPointerDown = (
    e: React.PointerEvent<HTMLButtonElement>,
    name: string,
  ) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragName(name);
    setExpandedColor(null);
    setConfirmDelete(null);
  };

  const handleGripPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragName) return;
    const y = e.clientY;
    const others = order.filter((n) => n !== dragName);
    let insertAt = others.length;
    for (let i = 0; i < others.length; i++) {
      const el = rowRefs.current[others[i]];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) {
        insertAt = i;
        break;
      }
    }
    const next = [...others];
    next.splice(insertAt, 0, dragName);
    if (next.join(' ') !== order.join(' ')) setOrder(next);
  };

  const handleGripPointerUp = () => {
    if (!dragName) return;
    setDragName(null);
    const serverOrder = labels.map((l) => l.name);
    if (order.join(' ') !== serverOrder.join(' ')) {
      onReorder(order);
    }
  };

  // Umbenennen (commit bei Blur/Enter)
  const commitRename = (oldName: string, raw: string) => {
    const next = raw.trim();
    if (!next || next === oldName) return;
    onRename(oldName, next);
  };

  // Neues Label
  const startAdd = () => {
    setNewName('');
    setNewColor(DEFAULT_NEW_COLOR);
    setExpandedColor(null);
    setConfirmDelete(null);
    setAdding(true);
  };
  const commitAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    onCreate(trimmed, newColor);
    setNewName('');
    setAdding(false);
  };

  return (
    <div className="px-4 py-3">
      <ul className="divide-y divide-gray-100">
        {order.map((name) => {
          const label = labelByName.get(name);
          if (!label) return null;
          const used = counts[name] ?? 0;
          const colorOpen = expandedColor === name;
          const confirming = confirmDelete === name;
          const dotColor = resolveLabelColor(label.color, name);

          return (
            <li
              key={name}
              ref={(el) => {
                rowRefs.current[name] = el;
              }}
              className={clsx(
                'py-2 transition-opacity',
                dragName === name && 'opacity-60',
              )}
            >
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Zum Sortieren ziehen"
                  onPointerDown={(e) => handleGripPointerDown(e, name)}
                  onPointerMove={handleGripPointerMove}
                  onPointerUp={handleGripPointerUp}
                  onPointerCancel={handleGripPointerUp}
                  className="shrink-0 p-1 -ml-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
                >
                  <GripVertical size={18} />
                </button>

                <button
                  type="button"
                  aria-label="Farbe ändern"
                  aria-expanded={colorOpen}
                  onClick={() =>
                    setExpandedColor(colorOpen ? null : name)
                  }
                  className="shrink-0 w-5 h-5 rounded-full ring-1 ring-black/10"
                  style={{ backgroundColor: dotColor }}
                />

                <input
                  key={name}
                  defaultValue={name}
                  aria-label="Label-Name"
                  maxLength={40}
                  onBlur={(e) => commitRename(name, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      e.currentTarget.value = name;
                      e.currentTarget.blur();
                    }
                  }}
                  className="flex-1 min-w-0 bg-transparent outline-none text-[15px] font-semibold text-secondary rounded px-1 py-1 -mx-1 focus:bg-gray-50"
                />

                <span className="shrink-0 text-xs font-bold tabular-nums text-gray-400">
                  {used}×
                </span>

                <button
                  type="button"
                  aria-label={`Label „${name}" löschen`}
                  onClick={() =>
                    used > 0
                      ? setConfirmDelete(confirming ? null : name)
                      : onDelete(name)
                  }
                  className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <Trash2 size={17} />
                </button>
              </div>

              {colorOpen && (
                <div className="pl-9 pr-1 py-2">
                  <TagColorSwatches
                    value={label.color}
                    onChange={(c) => {
                      onRecolor(name, c);
                      setExpandedColor(null);
                    }}
                  />
                </div>
              )}

              {confirming && (
                <div className="pl-9 pr-1 py-2 flex items-center justify-between gap-2">
                  <span className="text-[13px] text-text-muted">
                    Label aus {used} {used === 1 ? singular : plural} entfernen?
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(null)}
                      className="px-2.5 py-1.5 text-[13px] font-medium text-text-muted hover:bg-gray-100 rounded-lg"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmDelete(null);
                        onDelete(name);
                      }}
                      className="px-2.5 py-1.5 text-[13px] font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Neues Label */}
      {adding ? (
        <div className="mt-2 rounded-xl border border-gray-200 p-3 space-y-3">
          <TagColorSwatches value={newColor} onChange={setNewColor} />
          <div className="flex items-center gap-2">
            <input
              ref={newInputRef}
              value={newName}
              maxLength={40}
              placeholder="Label-Name…"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitAdd();
                }
                if (e.key === 'Escape') setAdding(false);
              }}
              className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-gray-200 bg-white outline-none text-[15px] text-secondary placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              onClick={() => setAdding(false)}
              aria-label="Abbrechen"
              className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-lg text-text-muted hover:bg-gray-100"
            >
              <X size={18} />
            </button>
            <button
              type="button"
              onClick={commitAdd}
              disabled={!newName.trim()}
              className="shrink-0 h-10 px-4 inline-flex items-center justify-center rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Hinzufügen
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={startAdd}
          className="mt-2 w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-primary/60 text-primary text-sm font-semibold hover:border-primary hover:bg-orange-50 transition-colors"
        >
          <Plus size={16} /> Neues Label
        </button>
      )}
    </div>
  );
}
